import { api } from '@/lib/api'

import { chainSurveyToSurvey, surveyDtoToSurvey, dtoToSurveyStats } from './adapters'
import {
	chainSurveySchema,
	surveyMetadataSchema,
	surveyResponseDtoSchema,
	surveyStatsResponseDtoSchema
} from './schemas'
import type { GetSurveysParams, SurveyMetadata, SurveyResponseDTO } from './types'
import { abi } from '@/abi/Survey4'
import { readContracts } from 'wagmi/actions'
import { wagmiConfig } from '@/config/wagmi-config'
import type { Hex } from 'viem'

export async function getSurveys(prefixUrl: string, { page, perPage, state, orderBy, direction }: GetSurveysParams) {
	const params = new URLSearchParams()

	params.append('page', page.toString())
	params.append('perPage', perPage.toString())
	state.forEach(s => params.append('state[]', s))

	if (orderBy) {
		params.append('orderBy', orderBy)
	}

	if (direction) {
		params.append('direction', direction)
	}

	const data = await api.get<SurveyResponseDTO>('pepper/surveys', { searchParams: params, prefixUrl }).json()

	const parsed = surveyResponseDtoSchema.parse(data)

	return {
		...parsed,
		surveys: parsed.surveys.map(surveyDtoToSurvey)
	}
}

export async function getSurveysStats(prefixUrl: string) {
	const data = await api.get('pepper/surveys/stats', { prefixUrl }).json()

	return dtoToSurveyStats(surveyStatsResponseDtoSchema.parse(data))
}

const CONTRACT_FUNCTIONS = [
	'questionId',
	'getAnswers',
	'startDate',
	'endDate',
	'isOpen',
	'totalVotes',
	'totalTokensUsed',
	'metadataURL',
	'votingCap'
] as const

export async function getSurveysFromChain(surveyAddresses: Hex[]) {
	const contracts = surveyAddresses.flatMap(address =>
		CONTRACT_FUNCTIONS.map(functionName => ({
			address,
			abi,
			functionName,
			args: [] as const
		}))
	)

	const data = await readContracts(wagmiConfig, { contracts })

	return surveyAddresses.map((address, index) => {
		const baseIndex = index * CONTRACT_FUNCTIONS.length
		const [question, answers, startDate, endDate, isOpen, totalVotes, totalTokensUsed, metadataURL, votingCap] =
			CONTRACT_FUNCTIONS.map((_, i) => data[baseIndex + i])

		return chainSurveyToSurvey(
			chainSurveySchema.parse({
				address,
				endDate: (endDate?.result as bigint) ?? 0n,
				answers: Array.isArray(answers?.result) ? answers.result : [],
				question: question?.result as string,
				startDate: (startDate?.result as bigint) ?? 0n,
				isOpen: isOpen?.result as boolean,
				totalVotes: (totalVotes?.result as bigint) ?? 0n,
				totalTokensUsed: (totalTokensUsed?.result as bigint) ?? 0n,
				metadataURL: metadataURL?.result as string,
				votingCap: (votingCap?.result as bigint) ?? 0n
			})
		)
	})
}

export async function putIpfsSurveyMetadata(prefixUrl: string, surveyMetadata: SurveyMetadata) {
	const uploadedMetadata = await api
		.put('ipfs', {
			body: JSON.stringify({
				type: 'survey-metadata',
				metadata: surveyMetadataSchema.parse(surveyMetadata)
			}),
			prefixUrl
		})
		.text()

	return uploadedMetadata
}
